/**
 * Google sign-in error copy — the ONE place a Firebase auth error code becomes
 * something a parent can read.
 *
 * WHY THIS FILE EXISTS. There are two sign-in paths and they failed
 * differently. The POPUP path (desktop) mapped errors inline in each page and
 * showed a message; the REDIRECT path (mobile + installed PWA, chosen by
 * `shouldUseGoogleRedirect`) swallowed the rejection into `console.error` and
 * showed nothing at all. A parent on an iPhone therefore saw a button that
 * appeared to do nothing — indistinguishable from a dead link, and impossible
 * for them to describe beyond "it doesn't work". The claim page is how a
 * family gets into the portal at all, so that population is every new family
 * from the Aug 31 cutover onward.
 *
 * Mapping in ONE place is the point, not tidiness: two inline copies are what
 * let the paths drift apart, and a second copy written for the redirect path
 * would have re-created the same divergence in a new place.
 *
 * The FALLBACK stays with the caller, deliberately. The right thing to tell
 * someone whose Google sign-in failed depends on what else that page offers —
 * the login page can suggest email/password, and the claim page can offer to
 * set a password, because it has that form on screen. Shared diagnosis,
 * page-specific advice.
 */

/**
 * The user shut the popup, or a second popup superseded the first. Not a
 * failure — showing an error for it would scold someone for changing their
 * mind. Redirect never produces these (there is no popup to close), so this
 * only ever fires on the desktop path.
 */
export function isCancelledByUser(error) {
	return (
		error?.code === 'auth/popup-closed-by-user' ||
		error?.code === 'auth/cancelled-popup-request'
	);
}

/**
 * A readable message for a Google sign-in failure, or `null` when the failure
 * is really a cancellation and nothing should be shown.
 *
 * @param error     the rejected Firebase auth error
 * @param fallback  page-appropriate copy for anything unrecognised
 */
export function googleSignInErrorMessage(error, fallback) {
	if (isCancelledByUser(error)) return null;

	switch (error?.code) {
		// The email already has an account created by a DIFFERENT provider.
		// This is the case a second OAuth provider multiplies, and on the
		// redirect path it was previously invisible.
		case 'auth/account-exists-with-different-credential':
			return 'An account already exists with this email using a different sign-in method. Please use email/password to log in.';

		// Firebase refuses OAuth from any origin not in the authorized-domains
		// list. Names the cause rather than blaming the parent — they cannot
		// fix it and should be told to get in touch.
		case 'auth/unauthorized-domain':
			return 'Sign-in is not available from this address. Please contact us at sing@coloryourvoice.com.';

		case 'auth/network-request-failed':
			return 'We could not reach Google. Please check your connection and try again.';

		case 'auth/user-disabled':
			return 'This account has been disabled. Please contact us at sing@coloryourvoice.com.';

		default:
			return fallback;
	}
}
